"use client"

import type React from "react"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"
import { useTextCylinder, type TextCylinderOptions } from "@/hooks/use-text-cylinder"
import { cn } from "@/lib/utils"
import styles from "./text-cylinder.module.css"

export interface TextCylinderProps<T = React.ReactNode> extends TextCylinderOptions<T> {
  className?: string
  faceClassName?: string
  containerClassName?: string
  onRotationComplete?: (index: number) => void
  perspective?: number
  radius?: number
  width?: number | string
  height?: number | string
  renderFace?: (item: T, index: number, isActive: boolean) => React.ReactNode
  debugMode?: boolean
}

export function TextCylinder<T = React.ReactNode>({
  items,
  autoRotate = false,
  autoRotateInterval = 3000,
  duration = 600,
  easing = "cubic-bezier(0.4, 0.0, 0.2, 1)",
  direction = "forward",
  className,
  faceClassName,
  containerClassName,
  onRotationComplete,
  perspective = 1000,
  radius = 100,
  width = "auto",
  height = "auto",
  renderFace,
  debugMode = false,
}: TextCylinderProps<T>) {
  const { state, controls, setIsAnimating, animationRef } = useTextCylinder({
    items,
    autoRotate,
    autoRotateInterval,
    duration,
    easing,
    direction,
  })

  const cylinderRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const prevIndexRef = useRef(state.currentIndex)
  const currentRotationRef = useRef(0)

  const anglePerFace = 360 / items.length

  useEffect(() => {
    if (!cylinderRef.current) return

    const cylinder = cylinderRef.current
    const targetIndex = state.currentIndex

    const forwardDistance = (targetIndex - prevIndexRef.current + items.length) % items.length
    const backwardDistance = (prevIndexRef.current - targetIndex + items.length) % items.length

    let rotationDelta: number
    if (forwardDistance <= backwardDistance) {
      rotationDelta = -forwardDistance * anglePerFace
    } else {
      rotationDelta = backwardDistance * anglePerFace
    }

    const targetRotation = currentRotationRef.current + rotationDelta

    if (prevIndexRef.current !== targetIndex) {
      if (animationRef.current) {
        try {
          // Commit current animated styles before canceling
          animationRef.current.commitStyles()
          animationRef.current.cancel()
        } catch (e) {
          // Animation might already be finished
        }
      }

      setIsAnimating(true)
      cylinder.classList.add(styles.animating || "animating")

      const distance = Math.min(forwardDistance, backwardDistance)
      const dynamicDuration = Math.max(duration * 0.5, Math.min(duration, duration * (distance / (items.length / 4))))

      const animation = cylinder.animate(
        [{ transform: `rotateX(${currentRotationRef.current}deg)` }, { transform: `rotateX(${targetRotation}deg)` }],
        {
          duration: dynamicDuration,
          easing,
          fill: "forwards",
        },
      )

      animationRef.current = animation

      animation.onfinish = () => {
        currentRotationRef.current = targetRotation
        prevIndexRef.current = targetIndex
        setIsAnimating(false)
        cylinder.classList.remove(styles.animating || "animating")
        onRotationComplete?.(targetIndex)
      }

      animation.oncancel = () => {
        cylinder.classList.remove(styles.animating || "animating")
      }

      return () => {
        if (animation.playState !== "finished") {
          try {
            animation.commitStyles()
            animation.cancel()
          } catch (e) {
            // Already canceled or finished
          }
        }
        cylinder.classList.remove(styles.animating || "animating")
      }
    }
  }, [
    state.currentIndex,
    anglePerFace,
    duration,
    easing,
    items.length,
    setIsAnimating,
    onRotationComplete,
    animationRef,
  ])

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.setProperty("--cylinder-perspective", `${perspective}px`)
    }
  }, [perspective])

  return {
    cylinderElement: (
      <div
        ref={containerRef}
        className={cn(styles.container, containerClassName)}
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
        }}
      >
        <div
          ref={cylinderRef}
          className={cn(styles.cylinder, className)}
          style={{
            transform: `rotateX(${-state.currentIndex * anglePerFace}deg)`,
          }}
        >
          {items.map((item, index) => {
            const rotateX = index * anglePerFace
            const translateZ = radius
            const isActive = index === state.currentIndex

            let isVisible = true
            let faceOpacity = 1

            if (!debugMode) {
              const currentRotation = state.currentIndex * anglePerFace
              const faceRotation = index * anglePerFace
              const angleDiff = ((faceRotation - currentRotation + 540) % 360) - 180
              isVisible = Math.abs(angleDiff) < 90
              faceOpacity = 1
            } else {
              faceOpacity = isActive ? 1 : 0.4
            }

            return (
              <div
                key={index}
                className={cn(styles.face, isVisible ? styles.visible : styles.hidden, faceClassName)}
                style={{
                  transform: `rotateX(${rotateX}deg) translateZ(${translateZ}px)`,
                  opacity: debugMode ? faceOpacity : undefined,
                }}
              >
                {renderFace ? renderFace(item, index, isActive) : (item as ReactNode)}
              </div>
            )
          })}
        </div>
      </div>
    ),
    state,
    controls,
  }
}

export function useTextCylinderPrimitive<T = React.ReactNode>(options: TextCylinderOptions<T>) {
  return useTextCylinder(options)
}
